import { useNavigate } from 'react-router-dom'
import { ArrowLeft, PlayCircle, Filter, ChevronDown, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, tapScale } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { Pagination, usePagination } from '@/components/shared/Pagination'
import { EmptyState } from '@/components/shared/EmptyState'

// Card display helpers
const getCardDisplay = (card: string) => {
  // Map emoji cards to rank and suit
  const cardMap: Record<string, { rank: string; suit: 'spades' | 'hearts' | 'clubs' | 'diamonds' }> = {
    '🂡': { rank: 'A', suit: 'spades' },
    '🂮': { rank: 'K', suit: 'spades' },
    '🂭': { rank: 'Q', suit: 'spades' },
    '🂫': { rank: 'J', suit: 'spades' },
    '🂪': { rank: '10', suit: 'spades' },
    '🂩': { rank: '9', suit: 'spades' },
    '🂨': { rank: '8', suit: 'spades' },
    '🂧': { rank: '7', suit: 'spades' },
    '🂦': { rank: '6', suit: 'spades' },
    '🂥': { rank: '5', suit: 'spades' },
    '🂤': { rank: '4', suit: 'spades' },
    '🂣': { rank: '3', suit: 'spades' },
    '🂢': { rank: '2', suit: 'spades' },
    '🂱': { rank: 'A', suit: 'hearts' },
    '🂾': { rank: 'K', suit: 'hearts' },
    '🂽': { rank: 'Q', suit: 'hearts' },
    '🂻': { rank: 'J', suit: 'hearts' },
    '🂺': { rank: '10', suit: 'hearts' },
    '🂹': { rank: '9', suit: 'hearts' },
    '🂸': { rank: '8', suit: 'hearts' },
    '🂷': { rank: '7', suit: 'hearts' },
    '🂶': { rank: '6', suit: 'hearts' },
    '🂵': { rank: '5', suit: 'hearts' },
    '🂴': { rank: '4', suit: 'hearts' },
    '🂳': { rank: '3', suit: 'hearts' },
    '🂲': { rank: '2', suit: 'hearts' },
    '🃑': { rank: 'A', suit: 'clubs' },
    '🃞': { rank: 'K', suit: 'clubs' },
    '🃝': { rank: 'Q', suit: 'clubs' },
    '🃛': { rank: 'J', suit: 'clubs' },
    '🃚': { rank: '10', suit: 'clubs' },
    '🃙': { rank: '9', suit: 'clubs' },
    '🃘': { rank: '8', suit: 'clubs' },
    '🃗': { rank: '7', suit: 'clubs' },
    '🃖': { rank: '6', suit: 'clubs' },
    '🃕': { rank: '5', suit: 'clubs' },
    '🃔': { rank: '4', suit: 'clubs' },
    '🃓': { rank: '3', suit: 'clubs' },
    '🃒': { rank: '2', suit: 'clubs' },
    '🃁': { rank: 'A', suit: 'diamonds' },
    '🃎': { rank: 'K', suit: 'diamonds' },
    '🃍': { rank: 'Q', suit: 'diamonds' },
    '🃋': { rank: 'J', suit: 'diamonds' },
    '🃊': { rank: '10', suit: 'diamonds' },
    '🃉': { rank: '9', suit: 'diamonds' },
    '🃈': { rank: '8', suit: 'diamonds' },
    '🃇': { rank: '7', suit: 'diamonds' },
    '🃆': { rank: '6', suit: 'diamonds' },
    '🃅': { rank: '5', suit: 'diamonds' },
    '🃄': { rank: '4', suit: 'diamonds' },
    '🃃': { rank: '3', suit: 'diamonds' },
    '🃂': { rank: '2', suit: 'diamonds' },
  }
  return cardMap[card] || { rank: '?', suit: 'spades' }
}

const getSuitSymbol = (suit: string) => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'clubs': return '♣'
    case 'diamonds': return '♦'
    default: return ''
  }
}

const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-900'

// Playing Card Component
function PlayingCard({ card, size = 'md' }: { card: string; size?: 'sm' | 'md' | 'lg' }) {
  const { rank, suit } = getCardDisplay(card)
  const suitSymbol = getSuitSymbol(suit)
  const colorClass = getSuitColor(suit)

  const sizeClasses = {
    sm: 'w-8 h-11',
    md: 'w-12 h-[66px]',
    lg: 'w-16 h-[88px]',
  }

  const fontSizes = {
    sm: { rank: 'text-xs', suit: 'text-[10px]', center: 'text-lg' },
    md: { rank: 'text-sm', suit: 'text-xs', center: 'text-2xl' },
    lg: { rank: 'text-base', suit: 'text-sm', center: 'text-4xl' },
  }

  return (
    <div className={cn(
      sizeClasses[size],
      "relative bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-lg shadow-lg border border-gray-200 overflow-hidden"
    )}>
      {/* Paper texture */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 100 100%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')] opacity-[0.03]" />

      {/* Top left corner */}
      <div className={cn("absolute top-0.5 left-1 flex flex-col items-center leading-none", colorClass)}>
        <span className={cn(fontSizes[size].rank, "font-bold font-serif")}>{rank}</span>
        <span className={fontSizes[size].suit}>{suitSymbol}</span>
      </div>

      {/* Center suit */}
      <div className={cn("absolute inset-0 flex items-center justify-center", colorClass)}>
        <span className={cn(fontSizes[size].center, "drop-shadow-sm")}>{suitSymbol}</span>
      </div>

      {/* Bottom right corner (rotated) */}
      <div className={cn("absolute bottom-0.5 right-1 flex flex-col items-center leading-none rotate-180", colorClass)}>
        <span className={cn(fontSizes[size].rank, "font-bold font-serif")}>{rank}</span>
        <span className={fontSizes[size].suit}>{suitSymbol}</span>
      </div>

      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent" />
    </div>
  )
}

// Expanded mock hands for pagination demo
const mockHands = [
  { id: '881234', date: '10:42', cards: ['🂡', '🂮'], board: ['🃑', '🂾', '🂵', '🃋', '🂢'], result: 'win', amount: 1240, pot: 2500, role: 'BTN', handName: '兩對' },
  { id: '881233', date: '10:38', cards: ['🃑', '🃞'], board: ['🂡', '🂶', '🃗', '🂨', '🃊'], result: 'loss', amount: -200, pot: 450, role: 'UTG', handName: '高牌' },
  { id: '881232', date: '10:35', cards: ['🂢', '🂣'], board: null, result: 'fold', amount: -50, pot: 120, role: 'BB', handName: null },
  { id: '881231', date: '10:30', cards: ['🃋', '🃝'], board: ['🃛', '🃚', '🃙', '🂷', '🂴'], result: 'win', amount: 4500, pot: 8000, role: 'SB', handName: '順子' },
  { id: '881230', date: '10:25', cards: ['🂵', '🂶'], board: ['🂡', '🂮', '🃑', '🂾', '🃗'], result: 'loss', amount: -800, pot: 1600, role: 'CO', handName: '高牌' },
  { id: '881229', date: '10:20', cards: ['🂡', '🂱'], board: ['🂮', '🂾', '🃞', '🂨', '🃘'], result: 'win', amount: 3200, pot: 6400, role: 'BTN', handName: '兩對' },
  { id: '881228', date: '10:15', cards: ['🃋', '🃛'], board: null, result: 'fold', amount: -100, pot: 200, role: 'UTG', handName: null },
  { id: '881227', date: '10:10', cards: ['🂮', '🂽'], board: ['🂡', '🂶', '🃗', '🂷', '🂴'], result: 'win', amount: 1800, pot: 3600, role: 'CO', handName: '一對' },
  { id: '881226', date: '10:05', cards: ['🂩', '🂹'], board: ['🂡', '🂮', '🃑', '🃚', '🂢'], result: 'loss', amount: -1500, pot: 3000, role: 'BB', handName: '高牌' },
  { id: '881225', date: '10:00', cards: ['🃑', '🃁'], board: ['🂡', '🃞', '🃎', '🂵', '🃕'], result: 'win', amount: 5500, pot: 11000, role: 'SB', handName: '葫蘆' },
  { id: '881224', date: '09:55', cards: ['🂧', '🂸'], board: null, result: 'fold', amount: -50, pot: 100, role: 'UTG', handName: null },
  { id: '881223', date: '09:50', cards: ['🂮', '🂱'], board: ['🂡', '🃑', '🃛', '🂶', '🂴'], result: 'win', amount: 2100, pot: 4200, role: 'BTN', handName: '一對' },
  { id: '881222', date: '09:45', cards: ['🃝', '🃍'], board: ['🂡', '🂮', '🃑', '🂾', '🃗'], result: 'loss', amount: -600, pot: 1200, role: 'CO', handName: '高牌' },
  { id: '881221', date: '09:40', cards: ['🂡', '🃑'], board: ['🂮', '🃞', '🂵', '🂶', '🂷'], result: 'win', amount: 8000, pot: 16000, role: 'BB', handName: '同花' },
  { id: '881220', date: '09:35', cards: ['🂣', '🂳'], board: null, result: 'fold', amount: -50, pot: 100, role: 'SB', handName: null },
]

export function HandHistoryScreen() {
  const navigate = useNavigate()
  const [expandedHand, setExpandedHand] = useState<string | null>(null)
  const [showEmptyDemo, setShowEmptyDemo] = useState(false)

  // Use pagination hook
  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems: paginatedHands,
    setCurrentPage,
    setPageSize,
  } = usePagination(mockHands, 5)

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full bg-background relative"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Header */}
      <motion.header variants={fadeInUp} className="flex-shrink-0 flex items-center gap-4 px-4 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40">
        <motion.button
          whileTap={tapScale}
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full glass-card hover:border-gold/30 transition-all"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </motion.button>
        <h1 className="font-display text-xl font-semibold text-foreground flex-1">手牌歷史</h1>
        <button
          onClick={() => setShowEmptyDemo(!showEmptyDemo)}
          className={cn("text-[10px] font-mono px-2 py-1 rounded-lg transition-all border border-dashed", showEmptyDemo ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-transparent text-muted-foreground/40 border-foreground/10")}
        >
          [DEV] 空狀態
        </button>
        <motion.button
          whileTap={tapScale}
          className="flex items-center gap-2 px-3 py-2 glass-card rounded-xl text-xs font-bold text-gold hover:border-gold/30 transition-all"
        >
          <Filter className="w-3.5 h-3.5" />
          篩選
        </motion.button>
      </motion.header>

      <motion.main
        variants={pageContainer}
        className="flex-1 overflow-y-auto px-4 py-4 scrollbar-hide space-y-4 pb-24 relative z-10"
      >
        {showEmptyDemo || paginatedHands.length === 0 ? (
          <EmptyState variant="no-history" />
        ) : (
        <>
        <motion.div variants={fadeInUp} className="text-xs text-muted-foreground font-bold uppercase tracking-wider px-1">今天</motion.div>

        {paginatedHands.map((hand) => (
          <motion.div
            key={hand.id}
            variants={cardReveal}
            onClick={() => setExpandedHand(expandedHand === hand.id ? null : hand.id)}
            className={cn(
              "glass-card rounded-2xl overflow-hidden transition-all cursor-pointer",
              expandedHand === hand.id && "border-gold/30"
            )}
          >
            {/* Main Card Content */}
            <div className="p-4">
              {/* Top Row: Result Badge + Time */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider",
                    hand.result === 'win' && "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
                    hand.result === 'loss' && "bg-rose-500/20 text-rose-400 border border-rose-500/30",
                    hand.result === 'fold' && "bg-foreground/5 text-muted-foreground border border-foreground/10"
                  )}>
                    {hand.result === 'win' ? '贏得' : hand.result === 'loss' ? '輸掉' : '棄牌'}
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">{hand.role}</span>
                  {hand.handName && (
                    <span className="text-xs text-gold font-medium">{hand.handName}</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground font-mono">{hand.date}</span>
              </div>

              {/* Cards Display */}
              <div className="flex items-center gap-6 mb-4">
                {/* Hole Cards */}
                <div className="flex items-center gap-1">
                  {hand.cards.map((card, i) => (
                    <PlayingCard key={i} card={card} size="md" />
                  ))}
                </div>

                {/* Board Cards (if exists) */}
                {hand.board && (
                  <>
                    <div className="w-px h-12 bg-foreground/10" />
                    <div className="flex items-center gap-1">
                      {hand.board.map((card, i) => (
                        <PlayingCard key={i} card={card} size="sm" />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Bottom Row: Amount + Pot */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "text-2xl font-bold font-mono",
                    hand.result === 'win' && "text-emerald-400",
                    hand.result === 'loss' && "text-rose-400",
                    hand.result === 'fold' && "text-muted-foreground"
                  )}>
                    {hand.amount > 0 ? '+' : ''}{(hand.amount / 100).toFixed(0)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    底池 <span className="text-foreground font-mono">${(hand.pot / 100).toFixed(0)}</span>
                  </span>
                </div>
                <ChevronDown className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform duration-200",
                  expandedHand === hand.id && "rotate-180"
                )} />
              </div>
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
              {expandedHand === hand.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-foreground/5"
                >
                  <div className="p-4 bg-surface-sunken/50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs text-muted-foreground font-medium">手牌 #{hand.id}</span>
                      <div className="flex items-center gap-2">
                        <motion.button
                          whileTap={tapScale}
                          onClick={(e) => {
                            e.stopPropagation()
                            const params = new URLSearchParams({
                              handId: hand.id,
                              cards: hand.cards.join(','),
                              ...(hand.board ? { board: hand.board.join(',') } : {}),
                              result: hand.result,
                              amount: String(hand.amount),
                              ...(hand.handName ? { handName: hand.handName } : {}),
                            })
                            navigate(`/disputes?${params.toString()}`)
                          }}
                          className="flex items-center gap-1.5 px-3 py-2 glass-card rounded-xl text-xs font-bold text-amber-400 hover:border-amber-400/30 transition-all"
                        >
                          <AlertTriangle className="w-3.5 h-3.5" />
                          回報問題
                        </motion.button>
                        <motion.button
                          whileTap={tapScale}
                          className="flex items-center gap-2 px-4 py-2 btn-primary rounded-xl text-xs font-bold"
                        >
                          <PlayCircle className="w-3.5 h-3.5" />
                          觀看回放
                        </motion.button>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs text-muted-foreground font-mono">
                      <p><span className="text-gold">Pre-flop:</span> Hero raises to 3BB, Villain calls</p>
                      <p><span className="text-gold">Flop:</span> Hero bets 5BB, Villain calls</p>
                      <p><span className="text-gold">Turn:</span> Check, Check</p>
                      <p><span className="text-gold">River:</span> Hero bets 15BB, Villain folds</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {/* Pagination */}
        <div className="pt-4 pb-20">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[5, 10, 20]}
          />
        </div>
        </>
        )}
      </motion.main>
    </motion.div>
  )
}
